import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Check,
  X,
  Loader2,
  Upload,
  Paperclip,
  Trash2,
  GraduationCap,
  Award,
  FileText,
  Download,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import SubTabs from "@/components/SubTabs";
import ModalPortal from "@/components/ModalPortal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 7-trinsskala — rækkefølge i dropdown (jf. #50)
const SKALA_FALLBACK = ["12", "10", "7", "4", "02", "00", "-3"];

// Karakterer der tæller som "bestået"-grøn (≥ 02).
const BESTAAET_KARAKTERER = new Set(["02", "4", "7", "10", "12"]);

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

const fmtGennemsnit = (n) =>
  n == null ? "—" : Number(n).toFixed(2).replace(".", ",");

// dd.mm.yyyy fra ISO (yyyy-mm-dd).
function fmtDato(iso) {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

// Dansk dato+tid fra ISO-timestamp (created_at), fx "18.06.2026 kl. 14:32".
function fmtDatoTid(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} kl. ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const inputCls =
  "text-sm bg-background border border-border rounded-lg px-2.5 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] disabled:opacity-60 transition-colors";

/* ─── Genbrugelige UI-byggesten ─── */

function FoldbarSektion({ titel, ikon: Ikon, badge, badgeGroen, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-secondary/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <ChevronDown
            className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`}
          />
          {Ikon && <Ikon className="w-4 h-4 text-[#FF2A6D]" />}
          <span className="text-sm font-bold tracking-wide uppercase text-foreground">{titel}</span>
          {badge != null && (
            <span
              className={`text-[13px] px-2 py-0.5 rounded-full tabular-nums ${
                badgeGroen
                  ? "bg-green-500/10 text-green-600 dark:text-green-400 font-semibold"
                  : "bg-secondary text-muted-foreground"
              }`}
            >
              {badge}
            </span>
          )}
        </div>
      </button>
      {open && <div className="border-t border-border px-5 py-4 space-y-4">{children}</div>}
    </div>
  );
}

/* #52: Lodret accordion-bar pr. modul. Header viser modul-navn + tæller
   "M2 · 1/2" og bliver GRØN når alle prøver i modulet er gennemført. Klik
   ekspanderer/folder. Da dette ikke er SubTabs implementeres grøn direkte. */
function ModulAccordionBar({ navn, done, total, open, onToggle, children }) {
  const komplet = total > 0 && done >= total;
  return (
    <div className="rounded-xl border border-border overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors ${
          komplet
            ? "bg-green-500/10 hover:bg-green-500/15"
            : "bg-secondary/40 hover:bg-secondary/60"
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <ChevronDown
            className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"} ${
              komplet ? "text-green-600 dark:text-green-400" : "text-muted-foreground"
            }`}
          />
          <span
            className={`text-sm font-semibold truncate ${
              komplet ? "text-green-600 dark:text-green-400" : "text-foreground"
            }`}
          >
            {navn}
          </span>
        </div>
        <span
          className={`text-[13px] tabular-nums px-2 py-0.5 rounded-full shrink-0 ${
            komplet
              ? "bg-green-500/15 text-green-600 dark:text-green-400 font-semibold"
              : "bg-secondary text-muted-foreground"
          }`}
        >
          {done}/{total}
        </span>
      </button>
      {open && <div className="border-t border-border px-3 py-3 space-y-2.5">{children}</div>}
    </div>
  );
}

function ProgressBar({ label, done, total, pct, ikkePaabegyndt }) {
  let andel;
  if (pct != null) andel = Math.max(0, Math.min(100, pct));
  else if (total > 0) andel = Math.round((done / total) * 100);
  else andel = 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[13px] font-semibold text-foreground">{label}</span>
        <span className="text-[13px] text-muted-foreground tabular-nums">
          {ikkePaabegyndt ? "Ikke påbegyndt" : pct != null ? `${andel}%` : `${done} / ${total}`}
        </span>
      </div>
      <div className="h-2 rounded-full bg-secondary overflow-hidden">
        <div
          className="h-full rounded-full bg-green-500 transition-[width] duration-500 ease-out"
          style={{ width: `${ikkePaabegyndt ? 0 : andel}%` }}
        />
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    "I gang": "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    "Klar til eksamen": "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    "Eksamen bestået": "bg-green-500/10 text-green-600 dark:text-green-400",
    "Eksamen ikke bestået": "bg-red-500/10 text-red-600 dark:text-red-400",
  };
  const cls = map[status] || "bg-secondary text-muted-foreground";
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[13px] font-semibold ${cls}`}>
      {status || "—"}
    </span>
  );
}

/* Lille toast — fixed top/midt, forsvinder af sig selv (styres af forælder). */
function Toast({ besked }) {
  if (!besked) return null;
  return (
    <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2 rounded-full bg-green-600 text-white text-sm font-semibold shadow-lg animate-in fade-in slide-in-from-top-2">
      <Check className="w-4 h-4" />
      {besked}
    </div>
  );
}

/* #56: upload-knap KUN på skriftlige/MC-prøver + speciale (praktiske prøver har
   intet at uploade). Regel pr. post: MC-type, speciale-type, eller navn der
   indeholder Teori/Skriftlig/MC. Bestået-fag (Fysik MC, Skinpen) har upload. */
function maaUploade(kind, post) {
  if (!post) return false;
  const navn = `${post.prove_navn || post.fag_navn || ""}`;
  const skriftligNavn = /\b(teori|skriftlig|mc)\b/i.test(navn);
  if (kind === "modulprove") {
    return post.type === "mc" || post.type === "speciale" || skriftligNavn;
  }
  if (kind === "bestaaet") {
    // Fysik MC + Speciale + Skinpen = skriftlige/afleverings-poster.
    return ["fysik_mc", "speciale", "skinpen"].includes(post.fag_key) || skriftligNavn;
  }
  // Årskarakterer/eksamenskarakterer: praktiske fag → ingen upload.
  return false;
}

/* Fil-håndtering for én post (modulprøve / fag). fil_ids gemmes på posten;
   filnavne for nyligt uploadede filer holdes i et lokalt map (id → navn).
   allowUpload styrer om "Upload fil"-knappen vises (#56). */
function FilListe({ filIds, navnMap, canEdit, authToken, onUpload, onDelete, allowUpload = true }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const download = (filId) => {
    // GET returnerer binær — åbn med Authorization via fetch + blob.
    setBusy(true);
    fetch(`${API}/admin/elever/karakter-fil/${filId}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Download fejl");
        return res.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      })
      .catch(() => alert("Kunne ikke hente fil"))
      .finally(() => setBusy(false));
  };

  const handlePick = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setBusy(true);
      try {
        await onUpload(file);
      } finally {
        setBusy(false);
        if (fileRef.current) fileRef.current.value = "";
      }
    }
  };

  return (
    <div className="space-y-1.5">
      {(filIds || []).map((id) => (
        <div key={id} className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => download(id)}
            className="flex items-center gap-1.5 text-[13px] text-blue-600 dark:text-blue-400 hover:underline"
          >
            <Paperclip className="w-3 h-3" />
            {navnMap[id] || "Vedhæftet fil"}
          </button>
          {canEdit && (
            <button
              type="button"
              onClick={() => onDelete(id)}
              className="text-muted-foreground hover:text-red-500"
              title="Slet fil"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
      {canEdit && allowUpload && (
        <label className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[13px] font-semibold bg-secondary text-muted-foreground hover:text-foreground cursor-pointer transition-colors w-fit">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          Upload fil
          <input ref={fileRef} type="file" className="hidden" onChange={handlePick} disabled={busy} />
        </label>
      )}
    </div>
  );
}

function StatusToggle({ value, canEdit, onChange }) {
  const btn = (val, label, activeCls) => (
    <button
      type="button"
      disabled={!canEdit}
      onClick={() => canEdit && onChange(value === val ? null : val)}
      className={`px-2.5 py-1 rounded-full text-[13px] font-semibold transition-colors disabled:cursor-default ${
        value === val ? activeCls : "bg-secondary text-muted-foreground hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
  return (
    <div className="flex items-center gap-1.5">
      {btn("bestaaet", "Bestået", "bg-green-500/10 text-green-600 dark:text-green-400")}
      {btn("ikke_bestaaet", "Ikke bestået", "bg-red-500/10 text-red-600 dark:text-red-400")}
    </div>
  );
}

/* ─── Foldbart KarakterKort (genbrugeligt på tværs af de tre sektioner) ─────────
   variant: "karakter" (ÅK/EK + modulprøve-MC kan bruge karakter? nej — se nedenfor)
   Vi modellerer felter via `kind`:
     - "modulprove": status-toggle + (mc-rigtige hvis type=mc) + dato + note + fil
     - "aak":        karakter-select + dato + underviser + note + fil
     - "ek":         karakter-select + dato + censor + note + fil
     - "bestaaet":   status-toggle + note + fil (ingen dato)
   Props:
     post           – den aktuelle post fra karakter_data (gemt tilstand)
     titel          – kort-titel
     kind           – se ovenfor
     canEdit        – read-only når false
     skala          – karakterskala
     navnMap        – fil_id → navn
     authToken      – til fil-download
     open           – om kortet er udfoldet
     onToggle()     – bruger klikker på kort-headeren
     onClose()      – luk uden gem
     onSave(draft)  – gem hele draft (merge sker i forælder)
     onDelete()     – nulstil post-værdier og gem
     onUpload/onDelete-fil – fil-handlers (arbejder på den GEMTE post, ikke draft)
     underviserNavn – prefill ved første karakter
   ───────────────────────────────────────────────────────────────────────────── */
function KarakterKort({
  post,
  titel,
  kind,
  canEdit,
  skala,
  navnMap,
  authToken,
  open,
  onToggle,
  onClose,
  onSave,
  onDelete,
  filHandlers,
  underviserNavn,
}) {
  // Lokal draft af kortets felter — commit først ved Gem.
  const [draft, setDraft] = useState(post);
  const [savingThis, setSavingThis] = useState(false);

  // Når kortet åbnes (open går true), nulstil draft til gemt tilstand.
  useEffect(() => {
    if (open) setDraft(post);
    // post er bevidst udeladt: vi vil kun re-seede ved open-skift, ikke ved
    // re-fetch under redigering.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setField = (patch) => setDraft((d) => ({ ...d, ...patch }));

  // #52: modulprøver bruger nu karakter (7-trin) for alle UNDTAGEN speciale,
  // som beholder kun status (bestået/ikke bestået).
  const erSpeciale = kind === "modulprove" && post.type === "speciale";

  // Er der ændringer ift. gemt tilstand? Note fjernet helt fra UI (#55, DEL E).
  const felter = (() => {
    if (kind === "modulprove")
      return erSpeciale
        ? ["status", "dato"]
        : ["karakter", "mc_rigtige", "dato"];
    if (kind === "aak") return ["karakter", "dato", "underviser"];
    if (kind === "ek") return ["karakter", "dato", "censor"];
    return ["status"]; // bestaaet
  })();
  const dirty = felter.some((f) => (draft[f] ?? "") !== (post[f] ?? ""));

  // Gem-knap aktiv: obligatoriske felter udfyldt.
  const harKarakter = draft.karakter != null && draft.karakter !== "";
  const harStatus = !!draft.status;
  const harDato = !!draft.dato;
  let canSave;
  if (kind === "bestaaet") canSave = harStatus; // disse poster har ingen dato
  else if (kind === "modulprove")
    canSave = erSpeciale ? harStatus : harKarakter && harDato; // speciale: status; øvrige: karakter+dato
  else canSave = harKarakter && harDato; // aak / ek

  // Resumé + farve for kompakt visning (baseret på GEMT post).
  const resume = kortResume(post, kind);
  const groen = kortGroen(post, kind);
  const roed = kortRoed(post, kind);
  const udfyldt = resume != null;

  const handleSave = async () => {
    if (!canSave || savingThis) return;
    const patch = {};
    felter.forEach((f) => {
      patch[f] = draft[f];
    });
    // Prefill underviser/censor (kun hvis ikke sat) ved aak/ek.
    if (kind === "aak" && !draft.underviser && underviserNavn) patch.underviser = underviserNavn;
    setSavingThis(true);
    try {
      await onSave(patch);
    } finally {
      setSavingThis(false);
    }
  };

  const handleClose = () => {
    if (dirty && !window.confirm("Kassér ændringer?")) return;
    onClose();
  };

  const handleDelete = async () => {
    if (!window.confirm("Slet karakter? Felterne nulstilles.")) return;
    setSavingThis(true);
    try {
      await onDelete();
    } finally {
      setSavingThis(false);
    }
  };

  // ── Kompakt (foldet) visning ──
  if (!open) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center justify-between gap-3 rounded-xl border border-border px-3 py-2.5 text-left transition-colors hover:bg-secondary/40 ${
          groen
            ? "bg-green-500/5 border-l-4 border-l-green-500"
            : roed
            ? "bg-red-500/5 border-l-4 border-l-red-500"
            : "bg-secondary/20"
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {udfyldt &&
            (groen ? (
              <Check className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />
            ) : (
              <X className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0" />
            ))}
          <span className="text-sm font-semibold text-foreground truncate">{titel}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {udfyldt ? (
            <span className="text-[13px] text-muted-foreground tabular-nums">{resume}</span>
          ) : (
            <span className="text-[12px] text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">
              Ikke udfyldt
            </span>
          )}
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        </div>
      </button>
    );
  }

  // ── Udfoldet (redigerings-)visning — kompakt, vandret række (#55, DEL F) ──
  return (
    <div
      className={`rounded-xl border border-border px-3 py-2 space-y-2 ${
        groen
          ? "bg-green-500/5 border-l-4 border-l-green-500"
          : roed
          ? "bg-red-500/5 border-l-4 border-l-red-500"
          : "bg-secondary/30"
      }`}
    >
      <button
        type="button"
        onClick={handleClose}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <span className="text-sm font-semibold text-foreground">{titel}</span>
        <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" />
      </button>

      {/* Felter pr. kind — alt på én vandret linje (wrapper på smal mobil). */}
      {kind === "modulprove" && erSpeciale && (
        <div className="flex items-center gap-x-4 gap-y-2 flex-wrap">
          {/* Speciale: status-toggle + (valgfri) dato. */}
          <span className="text-[13px] text-muted-foreground">Resultat:</span>
          <StatusToggle
            value={draft.status}
            canEdit={canEdit}
            onChange={(v) => setField({ status: v })}
          />
          <label className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
            Dato:
            <input
              type="date"
              value={draft.dato || ""}
              disabled={!canEdit}
              onChange={(e) => setField({ dato: e.target.value || null })}
              className={inputCls}
            />
          </label>
        </div>
      )}

      {kind === "modulprove" && !erSpeciale && (
        <div className="flex items-center gap-x-4 gap-y-2 flex-wrap">
          {/* MC: resultat-felt (skjul " ud af N" hvis mc_total er null). */}
          {post.type === "mc" && (
            <label className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
              Resultat:
              <input
                type="number"
                min="0"
                max={post.mc_total ?? undefined}
                value={draft.mc_rigtige ?? ""}
                disabled={!canEdit}
                onChange={(e) =>
                  setField({
                    mc_rigtige: e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                className={`${inputCls} w-16 text-right`}
              />
              {post.mc_total != null && <span>ud af {post.mc_total}</span>}
            </label>
          )}
          {/* Karakter (7-trin) — obligatorisk for alle ikke-speciale modulprøver. */}
          <label className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
            Karakter:
            <select
              value={draft.karakter ?? ""}
              disabled={!canEdit}
              onChange={(e) => {
                const v = e.target.value || null;
                const patch = { karakter: v };
                if (v && !draft.dato) patch.dato = todayISO();
                setField(patch);
              }}
              className={`${inputCls} w-20`}
            >
              <option value="">—</option>
              {skala.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
            Dato:
            <input
              type="date"
              value={draft.dato || ""}
              disabled={!canEdit}
              onChange={(e) => setField({ dato: e.target.value || null })}
              className={inputCls}
            />
          </label>
        </div>
      )}

      {(kind === "aak" || kind === "ek") && (
        <div className="flex items-center gap-x-4 gap-y-2 flex-wrap">
          <label className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
            Karakter:
            <select
              value={draft.karakter ?? ""}
              disabled={!canEdit}
              onChange={(e) => {
                const v = e.target.value || null;
                const patch = { karakter: v };
                if (v && !draft.dato) patch.dato = todayISO();
                setField(patch);
              }}
              className={`${inputCls} w-20`}
            >
              <option value="">—</option>
              {skala.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
            Dato:
            <input
              type="date"
              value={draft.dato || ""}
              disabled={!canEdit}
              onChange={(e) => setField({ dato: e.target.value || null })}
              className={inputCls}
            />
          </label>
          <label className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
            {kind === "ek" ? "Censor:" : "Underviser:"}
            <input
              type="text"
              value={(kind === "ek" ? draft.censor : draft.underviser) || ""}
              disabled={!canEdit}
              onChange={(e) =>
                setField(kind === "ek" ? { censor: e.target.value } : { underviser: e.target.value })
              }
              placeholder={kind === "ek" ? "Censor" : "Initialer / navn"}
              className={`${inputCls} w-40`}
            />
          </label>
        </div>
      )}

      {kind === "bestaaet" && (
        <div className="flex items-center gap-x-4 gap-y-2 flex-wrap">
          <span className="text-[13px] text-muted-foreground">Resultat:</span>
          <StatusToggle
            value={draft.status}
            canEdit={canEdit}
            onChange={(v) => setField({ status: v })}
          />
        </div>
      )}

      <FilListe
        filIds={post.fil_ids}
        navnMap={navnMap}
        canEdit={canEdit}
        authToken={authToken}
        onUpload={filHandlers.onUpload}
        onDelete={filHandlers.onDelete}
        allowUpload={maaUploade(kind, post)}
      />

      {/* Knaprække */}
      <div className="flex items-center justify-between gap-2 pt-0.5 flex-wrap">
        <div>
          {canEdit && udfyldt && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={savingThis}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-semibold text-red-600 dark:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Slet karakter
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={savingThis}
            className="px-4 py-1.5 rounded-full text-[13px] font-semibold bg-secondary text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          >
            Annullér
          </button>
          {canEdit && (
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave || savingThis}
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[13px] font-semibold transition-colors ${
                canSave && !savingThis
                  ? "bg-[#FF2A6D] text-white hover:bg-[#e02460]"
                  : "bg-secondary text-muted-foreground cursor-not-allowed"
              }`}
            >
              {savingThis ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              {udfyldt ? "Gem ændringer" : "Gem"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* Forbogstaver (initialer) af et navn, fx "Anne Berg" → "AB". */
function initialerAf(navn) {
  if (!navn) return "";
  return String(navn)
    .trim()
    .split(/\s+/)
    .map((ord) => ord.charAt(0).toUpperCase())
    .join("");
}

/* ── Resumé / farve-helpers (baseret på den GEMTE post) ── */
function kortResume(post, kind) {
  if (kind === "modulprove") {
    // Speciale: status-baseret. Øvrige: karakter-baseret (#52).
    if (post.type === "speciale") {
      if (!post.status) return null;
      return post.status === "bestaaet" ? "Bestået" : "Ikke bestået";
    }
    if (post.karakter == null || post.karakter === "") return null;
    const dele = [];
    if (post.type === "mc" && post.mc_rigtige != null)
      dele.push(post.mc_total != null ? `${post.mc_rigtige} ud af ${post.mc_total}` : `${post.mc_rigtige}`);
    dele.push(`${post.karakter}`);
    if (post.dato) dele.push(fmtDato(post.dato));
    return dele.join(" · ");
  }
  if (kind === "aak" || kind === "ek") {
    if (post.karakter == null || post.karakter === "") return null;
    const dele = [`${post.karakter}`];
    if (post.dato) dele.push(fmtDato(post.dato));
    // ÅK: vis underviser-initialer som sidste led (#55, DEL F).
    if (kind === "aak") {
      const init = initialerAf(post.underviser);
      if (init) dele.push(init);
    }
    return dele.join(" · ");
  }
  if (kind === "bestaaet") {
    if (!post.status) return null;
    return post.status === "bestaaet" ? "Bestået" : "Ikke bestået";
  }
  return null;
}

function kortGroen(post, kind) {
  if (kind === "bestaaet") return post.status === "bestaaet";
  if (kind === "modulprove" && post.type === "speciale") return post.status === "bestaaet";
  // Øvrige modulprøver + aak/ek: karakter-baseret.
  return BESTAAET_KARAKTERER.has(String(post.karakter ?? "").trim());
}

function kortRoed(post, kind) {
  if (kind === "bestaaet") return post.status === "ikke_bestaaet";
  if (kind === "modulprove" && post.type === "speciale") return post.status === "ikke_bestaaet";
  const k = String(post.karakter ?? "").trim();
  return k === "-3" || k === "00";
}

/* Modulprøve "gennemført" (#52, spec B): speciale → status bestået;
   øvrige → har en gyldig 7-trins-karakter (uanset bestået/dumpet). */
const erGennemfoertModulprove = (p) =>
  p.type === "speciale"
    ? p.status === "bestaaet"
    : SKALA_FALLBACK.includes(String(p.karakter ?? "").trim());

/* ── Tæller-helpers (done/total) pr. post-type ── */
const erUdfyldtKarakter = (p) => {
  const k = String(p.karakter ?? "").trim();
  return k !== "" && SKALA_FALLBACK.includes(k);
};
const erUdfyldtStatus = (p) => !!p.status;

/* ─── Klar-til-eksamen override-dialog (#52, spec E) ───
   Vises kun når der er mangler. Bekræft → markér klar med force:true. */
function KlarOverrideDialog({ mangler, busy, onClose, onConfirm }) {
  return (
    <ModalPortal onClose={onClose}>
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-base font-bold text-foreground">Ikke alt er udfyldt</h3>
            <p className="text-[13px] text-muted-foreground mt-1">
              Følgende mangler stadig at blive udfyldt:
            </p>
          </div>
        </div>
        <ul className="list-disc pl-6 space-y-1 text-[13px] text-foreground max-h-52 overflow-y-auto">
          {mangler.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
        <p className="text-sm text-foreground">
          Vil du alligevel markere eleven som klar til eksamen?
        </p>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-1.5 rounded-full text-[13px] font-semibold bg-secondary text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          >
            Annullér
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[13px] font-semibold bg-[#FF2A6D] text-white hover:bg-[#e02460] transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Markér klar til eksamen
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}

/* ─── Generér-karakterblad-dialog (#52, spec F + #55 frisør-variant) ───
   Henter læge-censorer (kun kosmetolog) + medarbejdere til dropdowns. POST'er
   felterne og modtager en PDF-blob som downloades. Backend vælger blad-variant
   ud fra elevens uddannelse_type — frontend skjuler blot Lægecensor for frisør,
   hvor Censor i stedet er fritekst med flere komma-separerede navne. */
function GenererBladDialog({
  itemId,
  authToken,
  elevNavn,
  uddannelseType,
  periodeFraDefault,
  periodeTilDefault,
  personnummer,
  onClose,
  onDone,
}) {
  const erFrisor = uddannelseType === "fri";
  const [eksamensdato, setEksamensdato] = useState(todayISO());
  const [periodeFra, setPeriodeFra] = useState(periodeFraDefault || "");
  const [periodeTil, setPeriodeTil] = useState(periodeTilDefault || "");
  const [laegecensor, setLaegecensor] = useState("");
  const [censor, setCensor] = useState("");
  const [ansvarlig, setAnsvarlig] = useState("");
  const [noter, setNoter] = useState("");

  const [laeger, setLaeger] = useState([]);
  const [medarbejdere, setMedarbejdere] = useState([]);
  const [busy, setBusy] = useState(false);
  const [fejl, setFejl] = useState(null);

  // Hent dropdown-data ved åbning. Lægecensorer kun nødvendige for kosmetolog.
  useEffect(() => {
    if (!itemId) return;
    let cancelled = false;
    const hdr = { headers: { Authorization: `Bearer ${authToken}` } };
    if (!erFrisor) {
      axios
        .get(`${API}/admin/elever/${itemId}/laegecensorer`, hdr)
        .then(
          (res) =>
            !cancelled &&
            setLaeger(
              Array.isArray(res.data) ? res.data : res.data?.laegecensorer || []
            )
        )
        .catch(() => !cancelled && setLaeger([]));
    }
    axios
      .get(`${API}/admin/medarbejdere-simple`, hdr)
      .then(
        (res) =>
          !cancelled &&
          setMedarbejdere(
            Array.isArray(res.data) ? res.data : res.data?.medarbejdere || []
          )
      )
      .catch(() => !cancelled && setMedarbejdere([]));
    return () => {
      cancelled = true;
    };
  }, [itemId, authToken, erFrisor]);

  const generer = async () => {
    if (busy || !itemId) return;
    setBusy(true);
    setFejl(null);
    try {
      const body = {
        eksamensdato,
        periode_fra: periodeFra,
        periode_til: periodeTil,
        censor, // fritekst (frisør: flere navne komma-separeret)
        ansvarlig_underviser: ansvarlig, // det valgte navn (string)
        noter,
      };
      // Lægecensor sendes kun for kosmetolog (frisør har ingen).
      if (!erFrisor) body.laegecensor = laegecensor;
      if (personnummer) body.personnummer = personnummer;
      const res = await axios.post(
        `${API}/admin/elever/${itemId}/karakterblad`,
        body,
        { headers: { Authorization: `Bearer ${authToken}` }, responseType: "blob" }
      );
      // Filnavn fra Content-Disposition, ellers fallback.
      const cd = res.headers?.["content-disposition"] || "";
      let filename = `Karakterblad - ${elevNavn || "Elev"} - ${eksamensdato}.pdf`;
      const m = /filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i.exec(cd);
      if (m && m[1]) filename = decodeURIComponent(m[1].trim());
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      await onDone();
    } catch (err) {
      // Fejl-svar kan være en blob (JSON) — forsøg at læse detail.
      let detail = err.response?.data?.detail || err.message;
      const blob = err.response?.data;
      if (blob instanceof Blob) {
        try {
          const txt = await blob.text();
          detail = JSON.parse(txt)?.detail || txt || detail;
        } catch {
          /* behold detail */
        }
      }
      setFejl("Kunne ikke generere karakterblad: " + detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalPortal onClose={busy ? undefined : onClose}>
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg p-5 space-y-4">
        <h3 className="text-base font-bold text-foreground">
          Generér karakterblad - {elevNavn || "Elev"}
        </h3>

        {fejl && (
          <div className="flex items-center gap-2 text-[13px] text-red-600 bg-red-500/10 rounded-xl px-3 py-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {fejl}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-[13px] text-muted-foreground">
            Eksamensdato
            <input
              type="date"
              value={eksamensdato}
              onChange={(e) => setEksamensdato(e.target.value)}
              className={inputCls}
            />
          </label>
          <div className="hidden sm:block" />
          <label className="flex flex-col gap-1 text-[13px] text-muted-foreground">
            Periode fra
            <input
              type="text"
              value={periodeFra}
              onChange={(e) => setPeriodeFra(e.target.value)}
              placeholder="mm/åååå"
              className={inputCls}
            />
          </label>
          <label className="flex flex-col gap-1 text-[13px] text-muted-foreground">
            Periode til
            <input
              type="text"
              value={periodeTil}
              onChange={(e) => setPeriodeTil(e.target.value)}
              placeholder="mm/åååå"
              className={inputCls}
            />
          </label>
          {!erFrisor && (
            <label className="flex flex-col gap-1 text-[13px] text-muted-foreground">
              Lægecensor
              <select
                value={laegecensor}
                onChange={(e) => setLaegecensor(e.target.value)}
                disabled={laeger.length === 0}
                className={inputCls}
              >
                {laeger.length === 0 ? (
                  <option value="">
                    Ingen lægeundervisere fundet. Sæt titel 'Lægeunderviser' på en medarbejder under Stamdata.
                  </option>
                ) : (
                  <>
                    <option value="">—</option>
                    {laeger.map((l) => (
                      <option key={l.item_id} value={l.navn}>
                        {l.navn}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </label>
          )}
          <label
            className={`flex flex-col gap-1 text-[13px] text-muted-foreground${
              erFrisor ? " sm:col-span-2" : ""
            }`}
          >
            Censor
            <input
              type="text"
              value={censor}
              onChange={(e) => setCensor(e.target.value)}
              placeholder={erFrisor ? "Censor (flere navne adskilles med komma)" : "Censor"}
              className={inputCls}
            />
            {erFrisor && (
              <span className="text-[11px] text-muted-foreground/80">
                Flere navne adskilles med komma
              </span>
            )}
          </label>
          <label className="flex flex-col gap-1 text-[13px] text-muted-foreground sm:col-span-2">
            Ansvarlig underviser
            <select
              value={ansvarlig}
              onChange={(e) => setAnsvarlig(e.target.value)}
              className={inputCls}
            >
              <option value="">—</option>
              {medarbejdere.map((m) => (
                <option key={m.item_id} value={m.navn}>
                  {m.navn}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[13px] text-muted-foreground sm:col-span-2">
            Evt. noter
            <textarea
              value={noter}
              onChange={(e) => setNoter(e.target.value)}
              placeholder="Evt. noter..."
              rows={2}
              className={`${inputCls} resize-y`}
            />
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-1.5 rounded-full text-[13px] font-semibold bg-secondary text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          >
            Annullér
          </button>
          <button
            type="button"
            onClick={generer}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[13px] font-semibold bg-[#FF2A6D] text-white hover:bg-[#e02460] transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
            Generér PDF
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}

/* ─── Hovedkomponent ─── */

export function KaraktererTab({ itemId, token, elevNavn, adminToken }) {
  const { user } = useAuth();
  const isAdmin = ["admin", "superadmin", "owner", "api_admin"].includes(user?.role);
  const kanRedigereElever = !!user?.rettigheder?.kan_redigere_elever;
  // Foreløbig adgang; backend's can_edit i GET-svaret er den autoritative kilde.
  const canEditLocal = isAdmin || kanRedigereElever;
  const underviserNavn = user?.name || "";

  // Admin-token bruges også under impersonation (eleven selv har ikke adgang).
  const authToken = adminToken || token;

  const [resolvedItemId, setResolvedItemId] = useState(itemId || null);
  const [data, setData] = useState(null); // hele GET-svaret (computed felter + karakter_data)
  const [kd, setKd] = useState(null); // gemt karakter_data (autoritativ baseline til merge)
  const [filNavne, setFilNavne] = useState({}); // fil_id → filnavn (kun nyligt uploadede)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [klarBusy, setKlarBusy] = useState(false);
  const [toast, setToast] = useState(null);

  // Hvilket kort er åbent? Globalt unikt id ("mp:<id>", "aak:<key>", ...). Kun ét ad gangen.
  const [openKort, setOpenKort] = useState(null);

  // Aktiv sub-fane for ÅK (EK er nu linje-format uden sub-faner, #55 DEL D).
  const [aakTab, setAakTab] = useState(null);

  // #52 modulprøve-accordion: hvilket modul er udfoldet (null = brug default).
  const [openModul, setOpenModul] = useState(null);

  // #55 (DEL D) EK linje-format: lokal draft af alle EK-karakterer (fag_key → karakter),
  // fælles EK-detaljer (eksamensdato/censor/note) og busy-flags.
  const [ekKarakterDraft, setEkKarakterDraft] = useState({});
  const [ekDetaljer, setEkDetaljer] = useState({ ek_eksamensdato: "", ek_censor: "", ek_note: "" });
  const [ekDetaljerBusy, setEkDetaljerBusy] = useState(false);
  const [ekGemBusy, setEkGemBusy] = useState(false);

  // #52 klar-til-eksamen override-dialog.
  const [klarDialogOpen, setKlarDialogOpen] = useState(false);

  // #52 karakterblad-state.
  const [blade, setBlade] = useState([]);
  const [bladDialogOpen, setBladDialogOpen] = useState(false);

  const toastTimer = useRef(null);
  const refetchRef = useRef(() => {});

  const canEdit = (data?.can_edit ?? canEditLocal) === true;

  const visToast = useCallback((besked) => {
    setToast(besked);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 1500);
  }, []);

  useEffect(() => () => toastTimer.current && clearTimeout(toastTimer.current), []);

  // Resolv itemId hvis ikke givet (impersonation-flow): /dashboard/student/okonomi
  // kører i elevens kontekst (token = impToken) og returnerer app_item_id.
  useEffect(() => {
    if (resolvedItemId) return;
    let cancelled = false;
    axios
      .get(`${API}/dashboard/student/okonomi`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (cancelled) return;
        const aid = res.data?.app_item_id;
        if (aid) setResolvedItemId(String(aid));
        else setError("Kunne ikke finde elevens item_id");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.detail || "Kunne ikke finde elev");
      });
    return () => {
      cancelled = true;
    };
  }, [token, resolvedItemId]);

  const applyResponse = useCallback((res) => {
    setData(res);
    setKd(res?.karakter_data || null);
  }, []);

  const fetchData = useCallback(
    (showSpinner = true) => {
      if (!resolvedItemId) return Promise.resolve();
      if (showSpinner) setLoading(true);
      return axios
        .get(`${API}/admin/elever/${resolvedItemId}/karakter-v2`, {
          headers: { Authorization: `Bearer ${authToken}` },
        })
        .then((res) => {
          applyResponse(res.data);
          setError(null);
        })
        .catch((err) => {
          const status = err.response?.status;
          if (status === 403) setError("Du har ikke adgang til at se karakterer for denne elev.");
          else setError(err.response?.data?.detail || err.message || "Kunne ikke hente karakterer");
        })
        .finally(() => {
          if (showSpinner) setLoading(false);
        });
    },
    [resolvedItemId, authToken, applyResponse]
  );

  refetchRef.current = () => fetchData(false);

  /* #52: hent listen over genererede karakterblade (versioner uden PDF-data). */
  const fetchBlade = useCallback(() => {
    if (!resolvedItemId) return Promise.resolve();
    return axios
      .get(`${API}/admin/elever/${resolvedItemId}/karakterblade`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => setBlade(Array.isArray(res.data) ? res.data : []))
      .catch(() => setBlade([]));
  }, [resolvedItemId, authToken]);

  useEffect(() => {
    fetchData(true);
    fetchBlade();
  }, [fetchData, fetchBlade]);

  /* #55 (DEL D): synkronisér EK-lokaldraft + fælles EK-detaljer fra gemt karakter_data
     hver gang baseline (kd) ændrer sig (load / re-fetch). */
  useEffect(() => {
    if (!kd) return;
    const draft = {};
    (kd.eksamenskarakterer || []).forEach((f) => {
      draft[f.fag_key] = f.karakter ?? "";
    });
    setEkKarakterDraft(draft);
    setEkDetaljer({
      ek_eksamensdato: kd.ek_eksamensdato || "",
      ek_censor: kd.ek_censor || "",
      ek_note: kd.ek_note || "",
    });
  }, [kd]);

  /* PUT hele karakter_data. `nextKd` er den fulde, merged struktur. Re-fetcher
     bagefter så beregnede felter (gennemsnit/progression/badge/klar_status) er
     autoritative. */
  const putKarakterData = useCallback(
    async (nextKd) => {
      if (!resolvedItemId) return;
      await axios.put(`${API}/admin/elever/${resolvedItemId}/karakter-v2`, nextKd, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      await refetchRef.current();
    },
    [resolvedItemId, authToken]
  );

  /* Simpel auto-save (ekstra fag + mødestabilitet-note) — disse er IKKE kort. */
  const autoSave = useCallback(
    async (updater) => {
      if (!canEdit || !kd) return;
      const next = updater(kd);
      // Optimistisk lokal opdatering så UI ikke "hopper".
      setKd(next);
      try {
        await putKarakterData(next);
      } catch (err) {
        setError(err.response?.data?.detail || "Kunne ikke gemme");
      }
    },
    [canEdit, kd, putKarakterData]
  );

  /* Gem ét kort: merge dets patch ind i den aktuelle karakter_data + PUT. */
  const gemKort = useCallback(
    async (sektion, matchKey, patch) => {
      if (!kd) return;
      const next = JSON.parse(JSON.stringify(kd));
      const liste = next[sektion] || [];
      const idx = liste.findIndex((x) =>
        sektion === "modulprover" ? x.id === matchKey : x.fag_key === matchKey
      );
      if (idx === -1) return;
      liste[idx] = { ...liste[idx], ...patch };
      try {
        await putKarakterData(next);
        setOpenKort(null);
        visToast("Gemt");
      } catch (err) {
        setError(err.response?.data?.detail || "Kunne ikke gemme");
      }
    },
    [kd, putKarakterData, visToast]
  );

  /* Slet ét korts værdier (behold fil_ids). */
  const sletKort = useCallback(
    async (sektion, matchKey) => {
      if (!kd) return;
      const next = JSON.parse(JSON.stringify(kd));
      const liste = next[sektion] || [];
      const idx = liste.findIndex((x) =>
        sektion === "modulprover" ? x.id === matchKey : x.fag_key === matchKey
      );
      if (idx === -1) return;
      const post = liste[idx];
      // Note røres ikke (#55, DEL E — UI sender ikke længere note).
      const nul = { dato: null };
      if (sektion === "modulprover") {
        // Speciale nulstiller status; øvrige nulstiller karakter + mc.
        if (post.type === "speciale") {
          nul.status = null;
        } else {
          nul.karakter = null;
          nul.mc_rigtige = null;
        }
      } else if (sektion === "aarskarakterer") {
        nul.karakter = null;
        nul.underviser = "";
      } else if (sektion === "eksamenskarakterer") {
        nul.karakter = null;
        nul.censor = "";
      } else if (sektion === "aarsk_bestaaet") {
        nul.status = null;
        delete nul.dato;
      }
      liste[idx] = { ...post, ...nul };
      try {
        await putKarakterData(next);
        setOpenKort(null);
        visToast("Slettet");
      } catch (err) {
        setError(err.response?.data?.detail || "Kunne ikke slette");
      }
    },
    [kd, putKarakterData, visToast]
  );

  /* ── Fil-operationer ── */
  const uploadFil = async (file, sektion, matchKey) => {
    if (!resolvedItemId) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/elever/${resolvedItemId}/karakter-fil`, formData, {
        headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "multipart/form-data" },
      });
      const { fil_id, filename } = res.data || {};
      if (!fil_id) return;
      setFilNavne((m) => ({ ...m, [fil_id]: filename || file.name }));
      // Tilføj fil_id til den GEMTE post + PUT (filer auto-gemmes uden for kort-flow).
      const next = JSON.parse(JSON.stringify(kd));
      const liste = next[sektion] || [];
      const idx = liste.findIndex((x) =>
        sektion === "modulprover" ? x.id === matchKey : x.fag_key === matchKey
      );
      if (idx !== -1) {
        liste[idx].fil_ids = [...(liste[idx].fil_ids || []), fil_id];
        await putKarakterData(next);
      }
    } catch (err) {
      alert("Kunne ikke uploade: " + (err.response?.data?.detail || err.message));
    }
  };

  const deleteFil = async (filId, sektion, matchKey) => {
    try {
      await axios.delete(`${API}/admin/elever/karakter-fil/${filId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const next = JSON.parse(JSON.stringify(kd));
      const liste = next[sektion] || [];
      const idx = liste.findIndex((x) =>
        sektion === "modulprover" ? x.id === matchKey : x.fag_key === matchKey
      );
      if (idx !== -1) {
        liste[idx].fil_ids = (liste[idx].fil_ids || []).filter((x) => x !== filId);
        await putKarakterData(next);
      }
    } catch (err) {
      alert("Kunne ikke slette: " + (err.response?.data?.detail || err.message));
    }
  };

  const filHandlersFor = (sektion, matchKey) => ({
    onUpload: (file) => uploadFil(file, sektion, matchKey),
    onDelete: (id) => deleteFil(id, sektion, matchKey),
  });

  /* #52: markér klar. force=true når der er mangler (override). Backenden
     400'er ikke længere; den sætter selv klar_med_mangler ud fra reelle mangler. */
  const markerKlar = async (force = false) => {
    if (!resolvedItemId || klarBusy) return;
    setKlarBusy(true);
    try {
      await axios.post(
        `${API}/admin/elever/${resolvedItemId}/karakter-v2/klar`,
        { force },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setKlarDialogOpen(false);
      await fetchData(false);
    } catch (err) {
      alert("Kunne ikke markere klar: " + (err.response?.data?.detail || err.message));
    } finally {
      setKlarBusy(false);
    }
  };

  /* Klik på "Markér klar": har eleven mangler → bekræftelses-dialog, ellers direkte. */
  const onKlarClick = () => {
    const m = data?.klar_status?.mangler || [];
    if (m.length > 0) setKlarDialogOpen(true);
    else markerKlar(false);
  };

  /* #55 (DEL D): gem fælles EK-detaljer (eksamensdato/censor/note) — ét PUT der
     fletter rod-felterne ind i karakter_data. */
  const gemEkDetaljer = async () => {
    if (!kd || ekDetaljerBusy || !canEdit) return;
    setEkDetaljerBusy(true);
    try {
      const next = JSON.parse(JSON.stringify(kd));
      next.ek_eksamensdato = ekDetaljer.ek_eksamensdato || null;
      next.ek_censor = ekDetaljer.ek_censor || "";
      next.ek_note = ekDetaljer.ek_note || "";
      await putKarakterData(next);
      visToast("Gemt");
    } catch (err) {
      setError(err.response?.data?.detail || "Kunne ikke gemme");
    } finally {
      setEkDetaljerBusy(false);
    }
  };

  /* #55 (DEL D): "Gem alle" — flet alle EK-karakterer fra lokal draft ind i
     kd.eksamenskarakterer og commit i ÉT PUT. */
  const gemAlleEk = async () => {
    if (!kd || ekGemBusy || !canEdit) return;
    setEkGemBusy(true);
    try {
      const next = JSON.parse(JSON.stringify(kd));
      next.eksamenskarakterer = (next.eksamenskarakterer || []).map((f) => {
        const val = ekKarakterDraft[f.fag_key];
        const karakter = val === "" || val == null ? null : val;
        const patch = { ...f, karakter };
        // Sæt dato ved første karakter (parallelt med kort-flowet).
        if (karakter && !f.dato) patch.dato = todayISO();
        return patch;
      });
      await putKarakterData(next);
      visToast("Gemt");
    } catch (err) {
      setError(err.response?.data?.detail || "Kunne ikke gemme");
    } finally {
      setEkGemBusy(false);
    }
  };

  /* #55 (DEL G): fortryd markering som klar til eksamen. Bevarer EK-data;
     badge → "I gang", EK-sektion lukkes. */
  const fortrydKlar = async () => {
    if (!resolvedItemId || klarBusy || !canEdit) return;
    if (!window.confirm("Fortryd markering som klar til eksamen?")) return;
    setKlarBusy(true);
    try {
      await axios.post(
        `${API}/admin/elever/${resolvedItemId}/karakter-v2/fortryd-klar`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      await fetchData(false);
      visToast("Markering fortrudt");
    } catch (err) {
      alert("Kunne ikke fortryde: " + (err.response?.data?.detail || err.message));
    } finally {
      setKlarBusy(false);
    }
  };

  /* #52: download en gemt karakterblad-version (base64→blob via fetch). */
  const downloadBlad = (bladId, filename) => {
    fetch(`${API}/admin/elever/karakterblad/${bladId}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Download fejl");
        return res.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || "Karakterblad.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => alert("Kunne ikke hente karakterblad"));
  };

  /* #52: slet en karakterblad-version (kun canEdit). */
  const sletBlad = async (bladId) => {
    if (!window.confirm("Slet dette karakterblad?")) return;
    try {
      await axios.delete(`${API}/admin/elever/karakterblad/${bladId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      await fetchBlade();
      visToast("Karakterblad slettet");
    } catch (err) {
      alert("Kunne ikke slette: " + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#FF2A6D] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Henter karakterer...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center gap-3 max-w-sm text-center">
          <AlertCircle className="w-8 h-8 text-[#FF2A6D]" />
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (!data || !kd) return null;

  const skala = data.skala?.length ? data.skala : SKALA_FALLBACK;
  const uddannelseLabel = data.uddannelse_type === "kos" ? "KOSMETOLOG" : "FRISØR";
  const prog = data.progression || {};
  const klarStatus = data.klar_status || {};
  const mangler = klarStatus.mangler || [];
  const manglerTekst = mangler.slice(0, 5).join(", ") + (mangler.length > 5 ? " m.fl." : "");
  // #52: knappen er ALDRIG disabled pga. mangler — kun når ikke kan redigere
  // eller eleven allerede er markeret klar. (kan_blive_klar er nu rådgivende.)
  const klarBtnDisabled = !canEdit || kd.klar_til_eksamen;
  const klarMedMangler = !!(data.klar_med_mangler || kd.klar_med_mangler);

  // Felles render-helper for ét KarakterKort.
  const renderKort = (sektion, kind, post, titel, matchKey) => {
    const kortId = `${sektion}:${matchKey}`;
    return (
      <KarakterKort
        key={kortId}
        post={post}
        titel={titel}
        kind={kind}
        canEdit={canEdit}
        skala={skala}
        navnMap={filNavne}
        authToken={authToken}
        underviserNavn={underviserNavn}
        open={openKort === kortId}
        onToggle={() => setOpenKort(kortId)}
        onClose={() => setOpenKort(null)}
        onSave={(patch) => gemKort(sektion, matchKey, patch)}
        onDelete={() => sletKort(sektion, matchKey)}
        filHandlers={filHandlersFor(sektion, matchKey)}
      />
    );
  };

  /* ── MODULPRØVER: gruppér pr. modul (lodret accordion, #52) ──
     Grupper bygges DYNAMISK fra data — modul-navn fra backend (modul_navn),
     fallback "Grundmodul" for M1 ellers "M{n}". Tæller = gennemførte prøver. */
  const mpListe = kd.modulprover || [];
  const modulGrupper = [];
  mpListe.forEach((mp) => {
    let g = modulGrupper.find((x) => x.modul === mp.modul);
    if (!g) {
      const navn = mp.modul_navn || (mp.modul === 1 ? "Grundmodul" : `M${mp.modul}`);
      g = { modul: mp.modul, navn, prover: [] };
      modulGrupper.push(g);
    }
    g.prover.push(mp);
  });
  modulGrupper.sort((a, b) => (a.modul || 0) - (b.modul || 0));
  const modulStat = (g) => {
    const done = g.prover.filter(erGennemfoertModulprove).length;
    return { done, total: g.prover.length, komplet: g.prover.length > 0 && done === g.prover.length };
  };
  // Default-udfoldet: første modul der ikke er fuldt gennemført; ellers første.
  const mpDefaultModul =
    (modulGrupper.find((g) => !modulStat(g).komplet) || modulGrupper[0])?.modul ?? null;
  const aktivtModul = openModul ?? mpDefaultModul;
  const mpDone = mpListe.filter(erGennemfoertModulprove).length;

  /* ── ÅRSKARAKTERER (#55, DEL H): gruppe-agnostisk. Fagene rendres som ÉN flad liste
     i backendens rækkefølge (frisør-parat — ingen hardcodede kos-keys der filtrerer fag
     væk). Special-faner (mødestabilitet / bestået / ekstra) bevares. ── */
  const aakListe = kd.aarskarakterer || [];
  const aakByKey = Object.fromEntries(aakListe.map((f) => [f.fag_key, f]));
  const bestListe = kd.aarsk_bestaaet || [];
  const ekstraListe = kd.ekstra_fag || [];
  const aakFagStat = { done: aakListe.filter(erUdfyldtKarakter).length, total: aakListe.length };
  const bestStat = { done: bestListe.filter(erUdfyldtStatus).length, total: bestListe.length };
  const ekstraStat = { done: ekstraListe.filter((f) => f.deltaget).length, total: ekstraListe.length };
  const aakTabs = [
    {
      key: "fag",
      label: `Fag · ${aakFagStat.done}/${aakFagStat.total}`,
      done: aakFagStat.done,
      total: aakFagStat.total,
    },
    { key: "modestabilitet", label: "Mødestabilitet", done: 1, total: 1 },
    ...(bestListe.length > 0
      ? [
          {
            key: "bestaaet",
            label: `Bestået/ikke bestået · ${bestStat.done}/${bestStat.total}`,
            done: bestStat.done,
            total: bestStat.total,
          },
        ]
      : []),
    {
      key: "ekstra",
      label: `Ekstra fag · ${ekstraStat.done}/${ekstraStat.total}`,
      done: ekstraStat.done,
      total: ekstraStat.total,
    },
  ];
  // Default: første fane ikke fuldt udfyldt (mødestabilitet tæller altid done).
  const aakDefault =
    (aakTabs.find((t) => t.key !== "modestabilitet" && t.done < t.total) || aakTabs[0])?.key ?? null;
  const aakActive = aakTab ?? aakDefault;

  /* ── EKSAMENSKARAKTERER: linje-format (#55, DEL D) — ÉN liste, ingen sub-faner.
     Rækkefølge = som backend leverer (uddannelses-drevet, frisør-parat). ÅK-kolonnen
     slår op i aarskarakterer pr. fag_key; ingen ÅK ⇒ "Ingen årskarakter". ── */
  const ekListe = kd.eksamenskarakterer || [];
  const ekDone = ekListe.filter(erUdfyldtKarakter).length;
  // "Gem alle" aktiv kun når draften afviger fra gemt tilstand.
  const ekDirty = ekListe.some(
    (f) => (ekKarakterDraft[f.fag_key] ?? "") !== (f.karakter ?? "")
  );

  return (
    <div className="space-y-4">
      <Toast besked={toast} />

      {/* Header + status-badge */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-sm font-bold tracking-wider uppercase text-muted-foreground">Karakter</h2>
          <p className="text-base font-semibold text-foreground flex items-center gap-2 flex-wrap">
            {elevNavn || "Elev"} <span className="text-muted-foreground">·</span>{" "}
            <span className="text-[#FF2A6D]">{uddannelseLabel}</span>
            <StatusBadge status={data.status_badge} />
            {klarMedMangler && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-semibold bg-amber-500/10 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="w-3 h-3" />
                Klargjort med manglende karakterer
              </span>
            )}
          </p>
        </div>
      </div>

      {error && data && (
        <div className="flex items-center gap-2 text-[13px] text-red-600 bg-red-500/10 rounded-xl px-4 py-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {!canEdit && (
        <p className="text-[13px] text-muted-foreground bg-secondary rounded-xl px-4 py-2">
          Skrivebeskyttet visning — du kan se men ikke redigere karakterer.
        </p>
      )}

      {/* A. PROGRESSIONSBAR */}
      <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
        <ProgressBar
          label="Modulprøver"
          done={prog.modulprover?.done}
          total={prog.modulprover?.total}
        />
        <ProgressBar
          label="Årskarakterer"
          done={prog.aarskarakterer?.done}
          total={prog.aarskarakterer?.total}
        />
        <ProgressBar
          label="Eksamen"
          done={prog.eksamen?.done}
          total={prog.eksamen?.total}
          ikkePaabegyndt={!prog.eksamen?.paabegyndt}
        />
        <div className="pt-2 border-t border-border">
          <ProgressBar label={`Samlet`} pct={prog.samlet_pct ?? 0} />
        </div>

        {/* B/D. GENNEMSNIT — ÅK, EK og HELHED (endelig) */}
        <div className="pt-3 border-t border-border grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-[12px] font-semibold tracking-wide uppercase text-muted-foreground">Gennemsnit ÅK</p>
            <p className="text-base font-bold text-foreground tabular-nums">{fmtGennemsnit(data.gennemsnit_aak)}</p>
          </div>
          <div>
            <p className="text-[12px] font-semibold tracking-wide uppercase text-muted-foreground">Gennemsnit EK</p>
            <p className="text-base font-bold text-foreground tabular-nums">{fmtGennemsnit(data.gennemsnit_ek)}</p>
          </div>
          <div>
            <p className="text-[12px] font-semibold tracking-wide uppercase text-muted-foreground">Helhed</p>
            <p className="text-lg font-bold text-[#FF2A6D] tabular-nums">{fmtGennemsnit(data.endelig_karakter)}</p>
          </div>
        </div>
      </div>

      {/* C. MODULPRØVER — lodret accordion pr. modul (#52) */}
      <FoldbarSektion
        titel="Modulprøver"
        ikon={GraduationCap}
        badge={`${prog.modulprover?.done ?? mpDone} / ${prog.modulprover?.total ?? mpListe.length}`}
        badgeGroen={mpListe.length > 0 && mpDone === mpListe.length}
        defaultOpen
      >
        {modulGrupper.length === 0 ? (
          <p className="text-[13px] text-muted-foreground text-center py-4">Ingen modulprøver</p>
        ) : (
          <div className="space-y-2.5">
            {modulGrupper.map((g) => {
              const s = modulStat(g);
              const aaben = String(aktivtModul) === String(g.modul);
              return (
                <ModulAccordionBar
                  key={g.modul}
                  navn={g.navn}
                  done={s.done}
                  total={s.total}
                  open={aaben}
                  onToggle={() => setOpenModul(aaben ? -1 : g.modul)}
                >
                  {g.prover.map((mp) =>
                    renderKort("modulprover", "modulprove", mp, mp.prove_navn, mp.id)
                  )}
                </ModulAccordionBar>
              );
            })}
          </div>
        )}
      </FoldbarSektion>

      {/* D. ÅRSKARAKTERER */}
      <FoldbarSektion
        titel="Årskarakterer (ÅK)"
        ikon={Award}
        badge={`${prog.aarskarakterer?.done ?? 0} / ${prog.aarskarakterer?.total ?? aakListe.length}`}
        badgeGroen={aakListe.length > 0 && (prog.aarskarakterer?.done ?? 0) === aakListe.length}
        defaultOpen
      >
        <SubTabs tabs={aakTabs} value={aakActive} onChange={setAakTab} />

        <div className="pt-1 space-y-2.5">
          {/* Fag — flad liste i backendens rækkefølge (kos + frisør, #55 DEL H). */}
          {aakActive === "fag" &&
            (aakListe.length === 0 ? (
              <p className="text-[13px] text-muted-foreground text-center py-4">Ingen årskarakterer</p>
            ) : (
              aakListe.map((f) =>
                renderKort("aarskarakterer", "aak", f, f.fag_navn, f.fag_key)
              )
            ))}

          {/* Mødestabilitet */}
          {aakActive === "modestabilitet" && (
            <div className="rounded-xl border border-border bg-secondary/30 p-3 space-y-2.5">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-sm font-semibold text-foreground">Mødestabilitet</span>
                <span className="inline-flex items-center gap-2 text-[13px]">
                  <span className="font-bold text-foreground tabular-nums">
                    {data.modestabilitet_live?.pct != null
                      ? `${Number(data.modestabilitet_live.pct).toFixed(1).replace(".", ",")}%`
                      : "—"}
                  </span>
                  <span className="text-muted-foreground">{data.modestabilitet_live?.label || ""}</span>
                </span>
              </div>
              <p className="text-[13px] text-muted-foreground italic">
                Autogenereret ud fra fremmøde — kan ikke redigeres.
              </p>
              <textarea
                value={kd.modestabilitet?.note || ""}
                disabled={!canEdit}
                onChange={(e) =>
                  autoSave((prev) => ({
                    ...prev,
                    modestabilitet: { ...(prev.modestabilitet || {}), note: e.target.value },
                  }))
                }
                placeholder="Kommentar til mødestabilitet..."
                rows={2}
                className={`${inputCls} w-full resize-y`}
              />
            </div>
          )}

          {/* Bestået / ikke bestået (Fysik MC + Speciale) */}
          {aakActive === "bestaaet" &&
            bestListe.map((f) =>
              renderKort("aarsk_bestaaet", "bestaaet", f, f.fag_navn, f.fag_key)
            )}

          {/* Ekstra fag (checkbox — auto-save) */}
          {aakActive === "ekstra" &&
            ekstraListe.map((f) => (
              <div key={f.fag_key} className="rounded-xl border border-border bg-secondary/30 p-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!f.deltaget}
                      disabled={!canEdit}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        autoSave((prev) => ({
                          ...prev,
                          ekstra_fag: prev.ekstra_fag.map((x) =>
                            x.fag_key === f.fag_key
                              ? {
                                  ...x,
                                  deltaget: checked,
                                  dato: checked ? x.dato || todayISO() : x.dato,
                                  underviser: checked ? x.underviser || underviserNavn : x.underviser,
                                }
                              : x
                          ),
                        }));
                      }}
                      className="accent-[#FF2A6D] w-4 h-4"
                    />
                    {f.fag_navn}
                  </label>
                  {f.deltaget && (
                    <div className="flex items-center gap-3 flex-wrap text-[13px] text-muted-foreground">
                      <input
                        type="date"
                        value={f.dato || ""}
                        disabled={!canEdit}
                        onChange={(e) =>
                          autoSave((prev) => ({
                            ...prev,
                            ekstra_fag: prev.ekstra_fag.map((x) =>
                              x.fag_key === f.fag_key ? { ...x, dato: e.target.value || null } : x
                            ),
                          }))
                        }
                        className={inputCls}
                      />
                      <input
                        type="text"
                        value={f.underviser || ""}
                        disabled={!canEdit}
                        onChange={(e) =>
                          autoSave((prev) => ({
                            ...prev,
                            ekstra_fag: prev.ekstra_fag.map((x) =>
                              x.fag_key === f.fag_key ? { ...x, underviser: e.target.value } : x
                            ),
                          }))
                        }
                        placeholder="Underviser"
                        className={`${inputCls} w-40`}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>

        {/* Gennemsnit ÅK (altid synlig) */}
        <div className="pt-3 border-t border-border flex items-center justify-between">
          <span className="text-sm font-semibold text-foreground">Gennemsnit årskarakter (ÅK)</span>
          <span className="text-base font-bold text-foreground">{fmtGennemsnit(data.gennemsnit_aak)}</span>
        </div>
      </FoldbarSektion>

      {/* E. KLAR TIL EKSAMEN — knap aldrig disabled pga. mangler (#52) */}
      <div className="bg-card border border-border rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm font-semibold text-foreground">Klar til eksamen</p>
          {kd.klar_til_eksamen ? (
            <p className="text-[13px] text-green-600 dark:text-green-400">
              Eleven er markeret klar til eksamen.
              {klarMedMangler && " (klargjort med manglende karakterer)"}
            </p>
          ) : mangler.length > 0 ? (
            <p className="text-[13px] text-muted-foreground">Mangler: {manglerTekst}</p>
          ) : (
            <p className="text-[13px] text-muted-foreground">Alle betingelser er opfyldt.</p>
          )}
        </div>
        {kd.klar_til_eksamen ? (
          /* #55 (DEL G): vis "Markeret klar" + Fortryd-knap. */
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold bg-green-500/10 text-green-600 dark:text-green-400">
              <Check className="w-3.5 h-3.5" />
              Markeret klar
            </span>
            {canEdit && (
              <button
                type="button"
                onClick={fortrydKlar}
                disabled={klarBusy}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold bg-secondary text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              >
                {klarBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                Fortryd
              </button>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={onKlarClick}
            disabled={klarBtnDisabled || klarBusy}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold transition-colors ${
              klarBtnDisabled || klarBusy
                ? "bg-secondary text-muted-foreground cursor-not-allowed"
                : "bg-[#FF2A6D] text-white hover:bg-[#e02460]"
            }`}
          >
            {klarBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Markér klar til eksamen
          </button>
        )}
      </div>

      {/* F. EKSAMENSKARAKTERER — kun når klar_til_eksamen */}
      {kd.klar_til_eksamen && (
        <FoldbarSektion
          titel="Eksamenskarakterer (EK)"
          ikon={Award}
          badge={`${prog.eksamen?.done ?? ekDone} / ${prog.eksamen?.total ?? ekListe.length}`}
          badgeGroen={ekListe.length > 0 && ekDone === ekListe.length}
          defaultOpen
        >
          {/* Fælles EK-detaljer: eksamensdato + censor (+ valgfri fælles note). */}
          <div className="rounded-xl border border-border bg-secondary/30 p-3 space-y-3">
            <div className="flex items-end gap-x-4 gap-y-2 flex-wrap">
              <label className="flex flex-col gap-1 text-[13px] text-muted-foreground">
                Eksamensdato
                <input
                  type="date"
                  value={ekDetaljer.ek_eksamensdato || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setEkDetaljer((d) => ({ ...d, ek_eksamensdato: e.target.value }))
                  }
                  className={inputCls}
                />
              </label>
              <label className="flex flex-col gap-1 text-[13px] text-muted-foreground flex-1 min-w-[12rem]">
                Censor
                <input
                  type="text"
                  value={ekDetaljer.ek_censor || ""}
                  disabled={!canEdit}
                  onChange={(e) => setEkDetaljer((d) => ({ ...d, ek_censor: e.target.value }))}
                  placeholder="Censor (flere navne komma-separeret)"
                  className={`${inputCls} w-full`}
                />
              </label>
              {canEdit && (
                <button
                  type="button"
                  onClick={gemEkDetaljer}
                  disabled={ekDetaljerBusy}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[13px] font-semibold bg-secondary text-foreground hover:bg-secondary/70 transition-colors disabled:opacity-50"
                >
                  {ekDetaljerBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Gem detaljer
                </button>
              )}
            </div>
            <label className="flex flex-col gap-1 text-[13px] text-muted-foreground">
              Fælles note
              <textarea
                value={ekDetaljer.ek_note || ""}
                disabled={!canEdit}
                onChange={(e) => setEkDetaljer((d) => ({ ...d, ek_note: e.target.value }))}
                placeholder="Fælles note for eksamen..."
                rows={2}
                className={`${inputCls} w-full resize-y`}
              />
            </label>
          </div>

          {/* Linje-liste: FAG | ÅRSKARAKTER (read-only) | EKSAMENSKARAKTER (dropdown). */}
          <div className="pt-1">
            {ekListe.length === 0 ? (
              <p className="text-[13px] text-muted-foreground text-center py-4">Ingen eksamensfag</p>
            ) : (
              <div className="overflow-hidden rounded-xl border border-border">
                <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 px-3 py-2 bg-secondary/40 text-[12px] font-semibold tracking-wide uppercase text-muted-foreground">
                  <span>Fag</span>
                  <span className="text-right w-24">Årskarakter</span>
                  <span className="text-right w-28">Eksamenskarakter</span>
                </div>
                <div className="divide-y divide-border">
                  {ekListe.map((f) => {
                    const aak = aakByKey[f.fag_key];
                    const aakVis =
                      aak && aak.karakter != null && aak.karakter !== ""
                        ? aak.karakter
                        : aak
                        ? "—"
                        : "Ingen årskarakter";
                    return (
                      <div
                        key={f.fag_key}
                        className="grid grid-cols-[1fr_auto_auto] gap-x-4 items-center px-3 py-2"
                      >
                        <span className="text-sm text-foreground truncate">{f.fag_navn}</span>
                        <span className="text-[13px] text-muted-foreground text-right w-24 tabular-nums">
                          {aakVis}
                        </span>
                        <select
                          value={ekKarakterDraft[f.fag_key] ?? ""}
                          disabled={!canEdit}
                          onChange={(e) =>
                            setEkKarakterDraft((d) => ({ ...d, [f.fag_key]: e.target.value }))
                          }
                          className={`${inputCls} w-28 justify-self-end`}
                        >
                          <option value="">--</option>
                          {skala.map((k) => (
                            <option key={k} value={k}>
                              {k}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* "Gem alle" — ét samlet PUT af alle EK-karakterer. */}
          {canEdit && ekListe.length > 0 && (
            <div className="flex justify-end pt-1">
              <button
                type="button"
                onClick={gemAlleEk}
                disabled={ekGemBusy || !ekDirty}
                className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[13px] font-semibold transition-colors ${
                  ekGemBusy || !ekDirty
                    ? "bg-secondary text-muted-foreground cursor-not-allowed"
                    : "bg-[#FF2A6D] text-white hover:bg-[#e02460]"
                }`}
              >
                {ekGemBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Gem alle
              </button>
            </div>
          )}

          <div className="pt-3 border-t border-border space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-foreground">Gennemsnit eksamenskarakter (EK)</span>
              <span className="text-base font-bold text-foreground">{fmtGennemsnit(data.gennemsnit_ek)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-foreground">Helhed (endelig karakter)</span>
              <span className="text-lg font-bold text-[#FF2A6D]">{fmtGennemsnit(data.endelig_karakter)}</span>
            </div>
          </div>

          {/* #56: Generér karakterblad placeret nederst i EK, lige under Helhed. */}
          {(canEdit || blade.length > 0) && (
            <div className="pt-3 mt-3 border-t border-border space-y-3">
              {canEdit && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setBladDialogOpen(true)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold bg-[#FF2A6D] text-white hover:bg-[#e02460] transition-colors"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    Generér karakterblad
                  </button>
                </div>
              )}

              {blade.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[12px] font-semibold text-muted-foreground">Tidligere genererede:</p>
                  {blade.map((b) => (
                    <div
                      key={b.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-border bg-secondary/30 px-3 py-2 flex-wrap"
                    >
                      <p className="text-[13px] text-foreground truncate min-w-0">{b.filename}</p>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={() => downloadBlad(b.id, b.filename)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[13px] font-semibold bg-secondary text-foreground hover:bg-secondary/70 transition-colors"
                        >
                          <Download className="w-3.5 h-3.5" />
                          Download
                        </button>
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => sletBlad(b.id)}
                            className="text-muted-foreground hover:text-red-500"
                            title="Slet karakterblad"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </FoldbarSektion>
      )}

      {/* Klar-til-eksamen override-dialog (#52) */}
      {klarDialogOpen && (
        <KlarOverrideDialog
          mangler={mangler}
          busy={klarBusy}
          onClose={() => setKlarDialogOpen(false)}
          onConfirm={() => markerKlar(true)}
        />
      )}

      {/* Generér-karakterblad-dialog (#52) */}
      {bladDialogOpen && (
        <GenererBladDialog
          itemId={resolvedItemId}
          authToken={authToken}
          elevNavn={elevNavn}
          uddannelseType={data?.uddannelse_type}
          periodeFraDefault={
            data?.elev_periode_fra || data?.start_date || ""
          }
          periodeTilDefault={
            data?.elev_periode_til || data?.slut_date || ""
          }
          personnummer={data?.personnummer || data?.cpr || ""}
          onClose={() => setBladDialogOpen(false)}
          onDone={async () => {
            await fetchBlade();
            setBladDialogOpen(false);
            visToast("Karakterblad genereret");
          }}
        />
      )}
    </div>
  );
}

export default KaraktererTab;
